import { Controller, useFormContext } from 'react-hook-form';
import { FormControlLabel, FormHelperText, Grid, MenuItem, Select, TextField } from '@mui/material';
import { useCreateDatasetMutation, useEditDatasetMutation, useGetUsersQuery } from '~/api/v2/hooks';
import { InfoFormLabel, SelectPlaceholder } from '~/components';
import { useFullName } from '~/hooks';
import { useHasLockedStatus } from '~/hooks/dataset';

const PrimaryDataStewardBlock = () => {
  const { data, isFetching } = useGetUsersQuery({});
  const { register, watch, control, setValue } = useFormContext();
  const secondaryDataStewards = watch('secondary_data_owners');
  const dataUploaders = watch('data_uploaders');
  const networkIdValue = watch('data_owner_network_id');
  const { fullName } = useFullName();
  const createDatasetMutation = useCreateDatasetMutation({ fixedCacheKey: `create-dataset` });
  const editDatasetMutation = useEditDatasetMutation({ fixedCacheKey: `edit-dataset` });
  const isSubmitting = editDatasetMutation[1].isLoading || createDatasetMutation[1].isLoading;
  const { hasLockedStatus } = useHasLockedStatus();

  return (
    <>
      <Grid size={6}>
        <FormControlLabel
          label={
            <InfoFormLabel
              label="DATA STEWARD"
              tooltipText="This is the name of the user that manages this dataset and can provide access to the dataset and its contents."
            />
          }
          required
          control={
            <Controller
              name="data_owner_name"
              control={control}
              render={({ field, fieldState }) => (
                <>
                  <FormHelperText error={!!fieldState.error}>{fieldState.error?.message as string}</FormHelperText>
                  <Select
                    {...field}
                    disabled={isFetching || isSubmitting || hasLockedStatus}
                    onChange={(event) => {
                      field.onChange(event);
                      const ntid = data?.find(({ id }) => id === event.target.value)?.profile?.NTID || '';
                      setValue(`data_owner_network_id`, ntid, {
                        shouldDirty: true,
                        shouldValidate: true,
                      });
                    }}
                    error={!!fieldState.error}
                    renderValue={() => {
                      if (!field.value) return <SelectPlaceholder>Select the primary data steward</SelectPlaceholder>;

                      return fullName(field.value);
                    }}
                  >
                    {data?.map(({ id, first_name, last_name }) =>
                      !secondaryDataStewards.concat(dataUploaders).includes(id) ? (
                        <MenuItem key={id} value={id}>
                          {first_name} {last_name}
                        </MenuItem>
                      ) : null,
                    )}
                  </Select>
                </>
              )}
            />
          }
        />
      </Grid>
      <Grid size={5}>
        <FormControlLabel
          label="DATA STEWARD NETWORK ID"
          required
          control={
            <TextField
              disabled
              data-testid={`data_owner_network_id.${networkIdValue}`}
              {...register('data_owner_network_id')}
            />
          }
        />
      </Grid>
    </>
  );
};

export default PrimaryDataStewardBlock;
